import { useCallback, useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useMobileDrawerContext } from 'src/contexts/MobileDrawerContext';
import {
  EventSortByFields,
  EventSortByFieldsToContentId,
  EventSortDisplayConfig,
  EventSortDisplayConfigToContentId,
  InventoryEventSortingCriteria,
  InventorySortingOrder,
} from 'src/contexts/MobileSortAndFilterContext';
import { useMobileSalesSortAndFilterContext } from 'src/contexts/MobileSortAndFilterContext/MobileSalesSortAndFilterContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Button, RadioGroup, Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { ContentId } from 'src/utils/constants/contentId';

import { InventoryConfigItem } from './components/InventoryConfigItem';
import { InventorySortingRadioItem } from './components/InventorySortingItem';
import { RIGHT_ARROW } from './InventoryListingSortingContent';
import * as styles from './InventoryListingSortingContent.css';

const sortingOrderOptionContent: Record<
  InventorySortingOrder,
  React.ReactNode
> = {
  ['asc']: <span>A {RIGHT_ARROW} Z</span>,
  ['desc']: <span>Z {RIGHT_ARROW} A</span>,
};

export const SaleEventSortingContent = () => {
  const { sortingCriteria, setSortingCriteria } =
    useMobileSalesSortAndFilterContext();
  const { toggle } = useMobileDrawerContext();

  const [localSortingCriteria, setLocalSortingCriteria] =
    useComputedState<InventoryEventSortingCriteria>(
      sortingCriteria as InventoryEventSortingCriteria
    );

  const onUpdateSortingField = useCallback(
    (field: EventSortByFields) => {
      const order = field === EventSortByFields.UnsoldListings ? 'desc' : 'asc';
      setLocalSortingCriteria((prev) => ({
        ...prev,
        sorting: { ...prev.sorting, sortBy: field, order },
      }));
    },
    [setLocalSortingCriteria]
  );

  const onUpdateSortingOrder = useCallback(
    (order: InventorySortingOrder) => {
      setLocalSortingCriteria((prev) => ({
        ...prev,
        sorting: { ...prev.sorting, order },
      }));
    },
    [setLocalSortingCriteria]
  );

  const onUpdateConfigView = useCallback(
    (config: EventSortDisplayConfig) => (isChecked: boolean) => {
      setLocalSortingCriteria((prev) => {
        const updated = new Set(prev.displayConfig.configs);
        if (isChecked) {
          updated.add(config);
        } else {
          updated.delete(config);
        }
        return { ...prev, displayConfig: { configs: Array.from(updated) } };
      });
    },
    [setLocalSortingCriteria]
  );

  const onSaveSortingCriteria = useCallback(() => {
    setSortingCriteria(localSortingCriteria);
    toggle();
  }, [localSortingCriteria, setSortingCriteria, toggle]);

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      gap="m"
      style={{ color: vars.color.textPrimary }}
    >
      <Stack
        direction="column"
        width="full"
        gap="l"
        style={{ flex: 1, overflowY: 'auto', padding: vars.spacing.sm }}
      >
        <Stack direction="column" width="full" gap="m">
          <span className={styles.sortAndFilterContentHeader}>
            <Content id={ContentId.SortEventsBy} />
          </span>
          <RadioGroup
            value={localSortingCriteria.sorting.sortBy}
            onValueChange={onUpdateSortingField}
          >
            <Stack direction="column" width="full" alignItems="center" gap="s">
              {Object.entries(EventSortByFieldsToContentId).map(
                ([field, contentId]) => {
                  if (
                    field === EventSortByFields.UnsoldListings ||
                    field === EventSortByFields.TicketCount
                  ) {
                    return null;
                  }
                  return (
                    <InventorySortingRadioItem
                      key={contentId}
                      contentId={contentId}
                      sortingField={field as EventSortByFields}
                      rightContent={
                        <SaleEventSortingRadioItemRightContent
                          currentField={field as EventSortByFields}
                          sortingCriteria={localSortingCriteria}
                          onUpdateSortingOrder={onUpdateSortingOrder}
                        />
                      }
                      onClick={() =>
                        onUpdateSortingField(field as EventSortByFields)
                      }
                    />
                  );
                }
              )}
            </Stack>
          </RadioGroup>
        </Stack>
        <Stack direction="column" width="full" gap="m">
          <span className={styles.sortAndFilterContentHeader}>
            <Content id={ContentId.ConfigureView} />
          </span>
          <Stack direction="column" width="full" alignItems="center" gap="s">
            {Object.entries(EventSortDisplayConfigToContentId).map(
              ([config, contentId]) => {
                if (config !== EventSortDisplayConfig.CollapseAllGroups) {
                  return null;
                }
                return (
                  <InventoryConfigItem
                    key={contentId}
                    contentId={contentId}
                    isChecked={localSortingCriteria.displayConfig.configs.includes(
                      config as EventSortDisplayConfig
                    )}
                    onUpdateConfig={onUpdateConfigView(
                      config as EventSortDisplayConfig
                    )}
                  />
                );
              }
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack justifyContent="spaceBetween" width="full" alignItems="center">
        <Button
          style={{ height: '40px' }}
          shape="rect"
          variant="outlineGray"
          onClick={toggle}
        >
          <Content id={ContentId.Cancel} />
        </Button>
        <Button
          style={{ height: '40px' }}
          shape="rect"
          onClick={onSaveSortingCriteria}
        >
          <Content id={ContentId.Save} />
        </Button>
      </Stack>
    </Stack>
  );
};

const SaleEventSortingRadioItemRightContent = ({
  currentField,
  sortingCriteria: { sorting },
  onUpdateSortingOrder,
}: {
  currentField: EventSortByFields;
  sortingCriteria: InventoryEventSortingCriteria;
  onUpdateSortingOrder: (order: InventorySortingOrder) => void;
}) => {
  const ordering = useMemo(
    () => (sorting.sortBy === currentField ? sorting.order : 'asc'),
    [currentField, sorting.order, sorting.sortBy]
  );

  if (currentField === EventSortByFields.EventDate) {
    return (
      <span className={styles.sotringOrderDescription}>
        <Content id={ContentId.Soonest} />
      </span>
    );
  }

  if (currentField === EventSortByFields.TicketCount) {
    return (
      <span className={styles.sotringOrderDescription}>
        <Content id={ContentId.Descending} />
      </span>
    );
  }

  if (sorting.sortBy !== currentField) {
    return undefined;
  }
  return (
    <PosSelect
      className={styles.sortingOrderTrigger}
      value={ordering}
      onChange={(newOrder: string) => {
        onUpdateSortingOrder(newOrder as InventorySortingOrder);
      }}
      valueOptionsContent={{
        ['asc']: '',
        ['desc']: '',
      }}
      valueOptionsContentOverride={sortingOrderOptionContent}
    />
  );
};
